import React from 'react'
import { CircularProgress, Tooltip } from '@mui/material'
import {
  Block as BlockIcon,
  CloudCircle as ProxyIcon,
  ContentCopy as CopyIcon,
  DarkMode as DarkModeIcon,
  DeleteOutline as DeleteIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  InfoOutlined as InfoIcon,
  Lan as IpIcon,
  LightMode as LightModeIcon,
  MailOutline as MailIcon,
  ModeOfTravel as GeoIcon,
  MonetizationOn as RefillIcon,
  NotificationAdd as NotifyIcon,
  Phone as PhoneIcon,
  QrCode2 as QrIcon,
  Search as SearchIcon,
  Today as RenewalIcon,
  Verified as VerifiedIcon,
  WorkspacePremium as DomainIcon,
} from '@mui/icons-material'
import { useDispatch, useSelector } from '../store'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { NotifyDialogV2 } from '../component/subscriber-v2/NotifyDialogV2'
import { ConfirmV2 } from '../component/ConfirmV2'
import { fetchTable, showSnackbar } from '../store/common/actionCreator'
import { getRefreshedToken } from '../store/auth/selector'
import { getTableLoading } from '../store/common/selector'
import { blockUsers, deleteUsers } from '../store/user/actionCreator'
import { postBlackListIpAddress } from '../store/blacklist/actionCreator'
import { Subscriber } from '../store/user/reducer'
import { EnumSubscriberStatus } from '../enum/statusSubscriber'
import { formatDate } from '../function/date'
import { api, Response } from '../function/axios'
import { useAdminTheme } from '../hook/useAdminTheme'
import s from './SubscribersPageV2.module.css'

// Same endpoint, page size and query params as SubscribersPage (DataTable):
// offset/limit, `search`, and `status` for the status filter.
const URL = '/users/clients'
const TABLE_ID = 'subscriberV2'
const LIMIT = 24

type StatusFilter = 'all' | `${EnumSubscriberStatus}`

const STATUS_META: Record<string, { label: string; color: string }> = {
  [EnumSubscriberStatus.PAID]: { label: 'Paid', color: 'var(--ok)' },
  [EnumSubscriberStatus.TRIAL]: { label: 'Trial', color: 'var(--primary)' },
  [EnumSubscriberStatus.BLOCKED]: { label: 'Blocked', color: 'var(--danger)' },
  [EnumSubscriberStatus.DELETED]: { label: 'Deleted', color: 'var(--text3)' },
}

const AVATAR_COLORS = ['#6366F1', '#EC4899', '#0EA5A5', '#F59E0B', '#2D89F4', '#10B981', '#8B5CF6', '#EF4444']

function fullName(row: Subscriber) {
  return [row.firstname, row.lastname].filter(Boolean).join(' ') || '—'
}

function initials(row: Subscriber) {
  const name = fullName(row)
  if (name === '—') return (row.email || '?').slice(0, 1).toUpperCase()
  return name.split(/[\s-]+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase()
}

function dateOrDash(value: string | Date | undefined | null, fmt: string) {
  if (!value) return '—'
  const formatted = formatDate(value, fmt)
  return formatted === 'undefined' || formatted === 'Invalid Date' ? '—' : formatted
}

function Flag({
  tip,
  kind,
  children,
  count,
  onClick,
}: {
  tip: string
  kind: 'bad' | 'good' | 'info'
  children: React.ReactNode
  count?: number
  onClick?: (e: React.MouseEvent) => void
}) {
  const kindClass = kind === 'bad' ? s.flagBad : kind === 'good' ? s.flagGood : s.flagInfo
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
      <span className={`${s.flag} ${kindClass} ${onClick ? s.flagClickable : ''}`} onClick={onClick}>
        {children}
        {count !== undefined && count > 0 && (
          <span className={`${s.badge} ${kind === 'info' ? s.badgeInfo : ''}`}>{count}</span>
        )}
      </span>
    </Tooltip>
  )
}

export function SubscribersPageV2() {
  const dispatch = useDispatch()
  const refreshedToken = useSelector(getRefreshedToken)
  const loading = useSelector(getTableLoading)
  const tableData = useSelector((state) => state.common.tables[TABLE_ID])

  const { theme, toggleTheme } = useAdminTheme()
  const [page, setPage] = React.useState(1)
  const [status, setStatus] = React.useState<StatusFilter>('all')
  const [searchInput, setSearchInput] = React.useState('')
  const [search, setSearch] = React.useState('')
  const [selected, setSelected] = React.useState<Subscriber[]>([])
  const [copiedUid, setCopiedUid] = React.useState<string | null>(null)
  const [kpis, setKpis] = React.useState<Record<string, number | null>>({})

  const [notifyMulti, setNotifyMulti] = React.useState(false)
  const [emailMulti, setEmailMulti] = React.useState(false)
  const [isDeleteUsers, setIsDeleteUsers] = React.useState(false)
  const [isBlockUsers, setIsBlockUsers] = React.useState(false)
  // the risk flag "IP used by other accounts" blacklists the IP and blocks the user — asked first
  const [blacklistAsk, setBlacklistAsk] = React.useState<{ ip: string; email: string } | null>(null)

  const rows: Subscriber[] = (tableData?.list as Subscriber[]) || []
  const total = tableData?.count ?? rows.length
  const pageCount = Math.max(1, Math.ceil(total / LIMIT))

  const params = React.useMemo(() => {
    const p: Record<string, any> = { offset: (page - 1) * LIMIT, limit: LIMIT }
    if (search) p.search = search
    if (status !== 'all') p.status = status
    return p
  }, [page, search, status])

  const refresh = React.useCallback(() => {
    if (refreshedToken === null) return
    dispatch(fetchTable(URL, params, TABLE_ID))
  }, [dispatch, params, refreshedToken])

  React.useEffect(() => {
    refresh()
  }, [refresh])

  // Debounced search, same param the old table sends.
  React.useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 400)
    return () => clearTimeout(id)
  }, [searchInput])

  // KPI counts: the list endpoint returns `count`, so a 1-row request per
  // status gives the totals without a new backend endpoint.
  React.useEffect(() => {
    if (refreshedToken === null) return
    const statuses: Array<[string, Record<string, any>]> = [
      ['all', {}],
      [EnumSubscriberStatus.PAID, { status: EnumSubscriberStatus.PAID }],
      [EnumSubscriberStatus.TRIAL, { status: EnumSubscriberStatus.TRIAL }],
      [EnumSubscriberStatus.BLOCKED, { status: EnumSubscriberStatus.BLOCKED }],
    ]
    statuses.forEach(([key, extra]) => {
      api({ method: 'GET', url: URL, params: { offset: 0, limit: 1, ...extra } })
        .then(({ data }: Response) => setKpis((k) => ({ ...k, [key]: typeof data?.count === 'number' ? data.count : null })))
        .catch(() => setKpis((k) => ({ ...k, [key]: null })))
    })
  }, [refreshedToken])

  const isSelected = (row: Subscriber) => selected.some((el) => el.id === row.id)

  const toggleRow = (row: Subscriber) => {
    if (isSelected(row)) {
      setSelected((state) => state.filter((el) => el.id !== row.id))
      return
    }
    if (row.isDeleted) {
      dispatch(showSnackbar('warning', 'You cannot send email to deleted subscriber'))
    }
    setSelected((state) => [...state, row])
  }

  const allOnPageSelected = rows.length > 0 && rows.every(isSelected)
  const toggleAll = () => {
    if (allOnPageSelected) {
      setSelected((state) => state.filter((el) => !rows.some((r) => r.id === el.id)))
    } else {
      setSelected((state) => [...state, ...rows.filter((r) => !state.some((el) => el.id === r.id))])
    }
  }

  const afterBulk = () => {
    setSelected([])
    refresh()
  }

  const handleRowClick = (row: Subscriber) => {
    window.open(`/users/subscriber_v2/${row.id}`, '_blank', 'noopener,noreferrer')
  }

  // copies a UID (or a Source IP) — the button turns green for a moment
  const copyUid = (e: React.MouseEvent, uid: string) => {
    e.stopPropagation()
    navigator.clipboard?.writeText(uid)
    setCopiedUid(uid)
    setTimeout(() => setCopiedUid((current) => (current === uid ? null : current)), 900)
  }

  const kpiValue = (key: string) => {
    const value = kpis[key]
    if (value === undefined) return '…'
    return value === null ? '—' : value.toLocaleString()
  }
  const kpiShare = (key: string) => {
    const all = kpis.all
    const part = kpis[key]
    if (!all || part === undefined || part === null) return ''
    return `${((part / all) * 100).toFixed(1)}% of total`
  }

  const renderRisk = (row: Subscriber) => {
    const info = row.registerInfo
    const ua = row.userAgent
    const flags: React.ReactNode[] = []

    if (
      info &&
      (info.email_quality_score === '0.00' ||
        info.email_deliverability === 'UNDELIVERABLE' ||
        info.email_is_disposable_email === true ||
        info.email_is_smtp_valid === false)
    ) {
      flags.push(
        <Flag key="email" kind="bad" tip={`E-mail score: ${info.email_quality_score}\nE-mail deliverability: ${info.email_deliverability}\nDisposable: ${info.email_is_disposable_email}\nSMTP valid: ${info.email_is_smtp_valid}`}>
          <MailIcon sx={{ fontSize: 15 }} />
        </Flag>,
      )
    }
    if (ua?.isEmailDomainValid === false) {
      flags.push(
        <Flag key="domain" kind="bad" tip="E-mail domain is not valid">
          <DomainIcon sx={{ fontSize: 15 }} />
        </Flag>,
      )
    }
    if (info && info.phone_valid === false) {
      flags.push(
        <Flag key="phone" kind="bad" tip={`Phone type: ${info.phone_type}\nPhone valid: ${info.phone_valid}`}>
          <PhoneIcon sx={{ fontSize: 15 }} />
        </Flag>,
      )
    }
    if (ua?.isIpPhoneMatches === false) {
      flags.push(
        <Flag key="geo" kind="bad" tip="Phone country code does not match with IP address location">
          <GeoIcon sx={{ fontSize: 15 }} />
        </Flag>,
      )
    }
    if (ua?.isProxy === 'yes') {
      flags.push(
        <Flag key="proxy" kind="bad" tip="User used proxy server for registration">
          <ProxyIcon sx={{ fontSize: 15 }} />
        </Flag>,
      )
    }
    if (!row.isDeleted && typeof ua?.ipMatchesCount === 'number' && ua.ipMatchesCount > 0) {
      flags.push(
        <Flag
          key="ip"
          kind="bad"
          count={ua.ipMatchesCount}
          tip={`This IP matches ${ua.ipMatchesCount} times\nClick to blacklist the IP and block the user`}
          onClick={(e) => {
            e.stopPropagation()
            setBlacklistAsk({ ip: ua.handlIP, email: row.email })
          }}
        >
          <IpIcon sx={{ fontSize: 15 }} />
        </Flag>,
      )
    }
    if (
      info &&
      info.email_quality_score !== '0.00' &&
      info.email_deliverability !== 'UNDELIVERABLE' &&
      info.email_is_disposable_email === false &&
      info.email_is_smtp_valid === true &&
      info.phone_valid === true &&
      ua?.isIpPhoneMatches === true
    ) {
      flags.push(
        <Flag key="ok" kind="good" tip="Looks like this user is not fake">
          <VerifiedIcon sx={{ fontSize: 15 }} />
        </Flag>,
      )
    }
    if (row.partners > 0) {
      flags.push(
        <Flag key="partners" kind="info" count={row.partners} tip={`Partners: ${row.partners}`}>
          <GroupIcon sx={{ fontSize: 15 }} />
        </Flag>,
      )
    }

    return flags.length ? <div className={s.flags}>{flags}</div> : <span className={s.muted}>—</span>
  }

  const renderSource = (row: Subscriber) => {
    const ua = row.userAgent
    if (row.isDeleted || !ua) return <span className={s.muted}>—</span>

    // one short pill; everything else (UTM fields, IP, GA) in a card on hover
    const known = (v?: string | null) => !!v && v !== 'Unknown'
    const isQr = !!ua.utmSource && String(ua.utmSource).toLowerCase() === 'qr'
    const kind = isQr ? 'qr' : known(ua.utmSource) ? 'utm' : ua.utmSource === 'Unknown' ? 'direct' : null

    const pill = kind === 'qr' ? (
      // QR quick registration (/join) sets utmSource to "qr" and utmCampaign to the QR code's label (or its token)
      <span className={`${s.pill} ${s.pillQr}`}><QrIcon sx={{ fontSize: 14 }} />{known(ua.utmCampaign) ? ua.utmCampaign : 'QR-code'}</span>
    ) : kind === 'utm' ? (
      <span className={s.pill}><span className={s.dot} style={{ background: 'var(--ok)' }} />{ua.utmSource}</span>
    ) : kind === 'direct' ? (
      <span className={s.pill}><span className={s.dot} style={{ background: 'var(--warn)' }} />Direct</span>
    ) : null

    const rows: [string, React.ReactNode][] = [
      ['Source', kind === 'qr' ? 'QR-code' : kind === 'utm' ? ua.utmSource : 'Direct — natural or a direct link'],
      ...(known(ua.utmCampaign) ? [['Campaign', ua.utmCampaign] as [string, React.ReactNode]] : []),
      ...(known(ua.utmMedium) ? [['Medium', ua.utmMedium] as [string, React.ReactNode]] : []),
      ...(known(ua.utmTerm) ? [['Term', ua.utmTerm] as [string, React.ReactNode]] : []),
      ...(known(ua.utmContent) ? [['Content', ua.utmContent] as [string, React.ReactNode]] : []),
      ...(known(ua.handlCustom1) ? [['handl custom 1', ua.handlCustom1] as [string, React.ReactNode]] : []),
      ['IP', known(ua.handlIP) ? <span className={s.mono}>{ua.handlIP}</span> : <span style={{ color: 'var(--danger)' }}>not detected</span>],
      ['GA client ID', known(ua.gaClientId) ? <span className={s.mono}>{ua.gaClientId}</span> : <span className={s.muted}>not detected</span>],
    ]
    const card = (
      <div className={`${s.theme} ${s.srcCard}`} data-theme={theme}>
        {rows.map(([k, v]) => <React.Fragment key={k}><span className={s.srcKey}>{k}</span><span className={s.srcVal}>{v}</span></React.Fragment>)}
      </div>
    )

    return (
      <Tooltip
        title={card}
        placement="bottom-start"
        enterDelay={150}
        componentsProps={{ tooltip: { sx: { background: 'transparent', p: 0, m: 0, maxWidth: 'none' } } }}
      >
        <span className={s.srcCell}>
          <span className={s.srcLine}>
            {pill || <span className={s.muted}>—</span>}
            <InfoIcon sx={{ fontSize: 15 }} className={s.srcInfo} />
          </span>
          {/* the IP stays in sight — it is what one blacklists */}
          {known(ua.handlIP) ? (
            <span className={s.srcLine}>
              <span className={`${s.pill} ${s.pillIp}`}><IpIcon sx={{ fontSize: 13 }} /><span className={s.mono}>{ua.handlIP}</span></span>
              <button
                className={`${s.copy} ${copiedUid === ua.handlIP ? s.copyDone : ''}`}
                onClick={(e) => copyUid(e, ua.handlIP)}
                aria-label="Copy IP address"
                title="Copy IP address"
              >
                <CopyIcon sx={{ fontSize: 13 }} />
              </button>
            </span>
          ) : ua.handlIP === 'Unknown' ? <span className={s.sub} style={{ color: 'var(--danger)' }}>IP not detected</span> : null}
        </span>
      </Tooltip>
    )
  }


  const pageButtons = React.useMemo(() => {
    const pages: Array<number | '…'> = []
    const add = (p: number) => {
      if (!pages.includes(p)) pages.push(p)
    }
    add(1)
    for (let p = Math.max(2, page - 1); p <= Math.min(pageCount - 1, page + 1); p++) {
      if (typeof pages[pages.length - 1] === 'number' && p - (pages[pages.length - 1] as number) > 1) pages.push('…')
      add(p)
    }
    if (pageCount > 1) {
      if (pageCount - (pages[pages.length - 1] as number) > 1) pages.push('…')
      add(pageCount)
    }
    return pages
  }, [page, pageCount])

  const header = (
    <div className={`${s.theme} ${s.header}`} data-theme={theme}>
      <div className={s.crumb}>
        <small>Users</small>
        <b>Subscriber Management</b>
      </div>
      <div className={s.spacer} />
      <label className={s.search}>
        <SearchIcon sx={{ fontSize: 17 }} />
        <input
          placeholder="Search by name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </label>
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={s.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  const from = total === 0 ? 0 : (page - 1) * LIMIT + 1
  const to = Math.min(page * LIMIT, total)

  return (
    <PrivatePage header={header}>
      <div className={`${s.theme} ${s.page}`} data-theme={theme}>
        <div className={s.kpis}>
          <div className={s.kpi}>
            <small>Total subscribers</small>
            <b>{kpiValue('all')}</b>
            <span>all statuses</span>
          </div>
          <div className={s.kpi}>
            <small>Paid</small>
            <b>{kpiValue(EnumSubscriberStatus.PAID)}</b>
            <span>{kpiShare(EnumSubscriberStatus.PAID)}</span>
          </div>
          <div className={s.kpi}>
            <small>Trial</small>
            <b>{kpiValue(EnumSubscriberStatus.TRIAL)}</b>
            <span>{kpiShare(EnumSubscriberStatus.TRIAL)}</span>
          </div>
          <div className={s.kpi}>
            <small>Blocked</small>
            <b style={{ color: 'var(--danger)' }}>{kpiValue(EnumSubscriberStatus.BLOCKED)}</b>
            <span>{kpiShare(EnumSubscriberStatus.BLOCKED)}</span>
          </div>
        </div>

        <div className={s.sec}>
          <h2>Subscribers</h2>
          <span className={s.count}>· {total.toLocaleString()} results</span>
          <div className={s.spacer} />
          <div className={s.chips}>
            {(['all', EnumSubscriberStatus.TRIAL, EnumSubscriberStatus.PAID, EnumSubscriberStatus.BLOCKED, EnumSubscriberStatus.DELETED] as StatusFilter[]).map((key) => (
              <button
                key={key}
                className={`${s.chip} ${status === key ? s.chipOn : ''}`}
                onClick={() => {
                  setStatus(key)
                  setPage(1)
                }}
              >
                {key !== 'all' && <span className={s.dot} style={{ background: STATUS_META[key].color }} />}
                {key === 'all' ? 'All' : STATUS_META[key].label}
              </button>
            ))}
          </div>
        </div>

        {selected.length > 0 && (
          <div className={s.bulk}>
            <span>{selected.length} selected</span>
            <div className={s.spacer} />
            <button className={s.act} onClick={() => setIsBlockUsers(true)}>
              <BlockIcon sx={{ fontSize: 16 }} />
              Block
            </button>
            <button className={s.act} onClick={() => setNotifyMulti(true)}>
              <NotifyIcon sx={{ fontSize: 16 }} />
              Notify
            </button>
            <button className={s.act} onClick={() => setEmailMulti(true)}>
              <EmailIcon sx={{ fontSize: 16 }} />
              E-mail
            </button>
            <button className={`${s.act} ${s.actDanger}`} onClick={() => setIsDeleteUsers(true)}>
              <DeleteIcon sx={{ fontSize: 16 }} />
              Delete
            </button>
          </div>
        )}

        <div className={s.card}>
          <div className={s.scroll}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox" className={s.cb} checked={allOnPageSelected} onChange={toggleAll} />
                  </th>
                  <th>UID</th>
                  <th>Subscriber</th>
                  <th>Contact</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>Renewal</th>
                  <th>Risk</th>
                  <th>Registered</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const meta = STATUS_META[row.status] || { label: row.status, color: 'var(--text3)' }
                  const lastActive = dateOrDash(row.sessions?.[0]?.lastActiveDate, 'dd/MM/yyyy')
                  return (
                    <tr key={row.id} className={isSelected(row) ? s.sel : ''} onClick={() => handleRowClick(row)}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className={s.cb} checked={isSelected(row)} onChange={() => toggleRow(row)} />
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Tooltip title={row.uid || ''}>
                          <div className={s.uidCell}>
                            <span className={s.uid}>{row.uid}</span>
                            {row.uid && (
                              <button
                                className={`${s.copy} ${copiedUid === row.uid ? s.copyDone : ''}`}
                                onClick={(e) => copyUid(e, row.uid)}
                                aria-label="Copy UID"
                              >
                                <CopyIcon sx={{ fontSize: 13 }} />
                              </button>
                            )}
                          </div>
                        </Tooltip>
                      </td>
                      <td>
                        <div className={s.who}>
                          {row.avatar ? (
                            <img className={s.ava} src={row.avatar} alt="" />
                          ) : (
                            <div className={s.ava} style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                              {initials(row)}
                            </div>
                          )}
                          <b>{fullName(row)}</b>
                        </div>
                      </td>
                      <td className={s.muted}>
                        <div className={s.ell} title={row.phone || row.email}>
                          {row.phone || row.email}
                        </div>
                      </td>
                      <td>
                        <div className={s.ell} style={{ maxWidth: 110 }} title={row.country || ''}>
                          {row.country || '—'}
                        </div>
                      </td>
                      <td>
                        <span className={s.status}>
                          <span className={s.dot} style={{ background: meta.color }} />
                          {meta.label}
                        </span>
                      </td>
                      <td>
                        <div className={s.renew}>
                          <Tooltip title={`Autorenewal is switched ${row.isAutoRenewal ? 'on' : 'off'}`}>
                            <span className={`${s.tog} ${row.isAutoRenewal ? s.togOn : s.togOff}`}>
                              <RenewalIcon sx={{ fontSize: 13 }} />
                            </span>
                          </Tooltip>
                          <Tooltip title={`Autorefill is switched ${row.isAutoRefill ? 'on' : 'off'}`}>
                            <span className={`${s.tog} ${row.isAutoRefill ? s.togOn : s.togOff}`}>
                              <RefillIcon sx={{ fontSize: 13 }} />
                            </span>
                          </Tooltip>
                          <span>{dateOrDash(row.packageEndDate, 'dd/MM/yyyy')}</span>
                        </div>
                      </td>
                      <td>{renderRisk(row)}</td>
                      <td className={s.nowrap}>
                        {dateOrDash(row.createdTime, 'dd/MM/yyyy')}{' '}
                        <span className={s.muted} style={{ fontSize: 11.5 }}>{dateOrDash(row.createdTime, 'HH:mm')}</span>
                        <span className={s.sub}>{lastActive === '—' ? 'never active' : `active ${lastActive}`}</span>
                      </td>
                      <td>{renderSource(row)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {!loading && rows.length === 0 && <div className={s.empty}>No subscribers with selected filters</div>}
            {loading && (
              <div className={s.loading}>
                <CircularProgress size={28} />
              </div>
            )}
          </div>
          <div className={s.foot}>
            <span>
              {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
            </span>
            <div className={s.pager}>
              <button className={s.pg} disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
              {pageButtons.map((p, idx) =>
                p === '…' ? (
                  <button key={`gap${idx}`} className={s.pg} disabled>…</button>
                ) : (
                  <button key={p} className={`${s.pg} ${p === page ? s.pgOn : ''}`} onClick={() => setPage(p)}>
                    {p}
                  </button>
                ),
              )}
              <button className={s.pg} disabled={page >= pageCount} onClick={() => setPage(page + 1)}>›</button>
            </div>
          </div>
        </div>
      </div>

      {notifyMulti && (
        <NotifyDialogV2 mode="notification" subscribers={selected} onClose={() => setNotifyMulti(false)} />
      )}
      {emailMulti && <NotifyDialogV2 mode="email" subscribers={selected} onClose={() => setEmailMulti(false)} />}
      {isDeleteUsers && (
        <ConfirmV2
          onYes={() => dispatch(deleteUsers(selected.map((el) => el.id).join(','), afterBulk))}
          onClose={() => setIsDeleteUsers(false)}
          header={`Delete ${selected.length === 1 ? 'the selected user' : `${selected.length} selected users`}?`}
        />
      )}
      {blacklistAsk && (
        <ConfirmV2
          header={`Blacklist ${blacklistAsk.ip} and block ${blacklistAsk.email || 'this user'}?`}
          yesLabel="Blacklist & block"
          danger
          onYes={() => dispatch(postBlackListIpAddress({ ipAddress: blacklistAsk.ip, blockUser: true }))}
          onClose={() => setBlacklistAsk(null)}
        />
      )}
      {isBlockUsers && (
        <ConfirmV2
          onYes={() => dispatch(blockUsers(selected.map((el) => el.id).join(','), afterBulk))}
          onClose={() => setIsBlockUsers(false)}
          header={`Block ${selected.length === 1 ? 'the selected user' : `${selected.length} selected users`}? They will not be able to sign in.`}
        />
      )}
    </PrivatePage>
  )
}
